// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useState, useEffect } from 'react'
import { Sparkles, X, Loader2, Send, ShieldCheck } from 'lucide-react'
import * as api from '../../api/client'
import { tokenizePHI, detokenize, validateAIOutput, type TokenMap } from './aiClient'
import { QUICK_PROMPTS } from './aiPrompts'

const QUICK = [
  { label: 'Drug monitoring', type: 'chat' as const, prompt: QUICK_PROMPTS.drugMonitoring },
  { label: 'Write exam note', type: 'field_assist' as const, prompt: QUICK_PROMPTS.examNote },
  { label: 'ICD-10 lookup', type: 'icd10' as const, prompt: QUICK_PROMPTS.icd10 },
  { label: 'DDI screen', type: 'ddi' as const, prompt: QUICK_PROMPTS.ddi },
]

const CONSENT_KEY = 'carerx_ai_consent'

/**
 * AI clinical-assist panel — faithful to the POT app: client-side PHI tokenization before
 * send + detokenize on response (the FIRST layer; the server tokenizer is the backstop),
 * a one-time consent gate, and AI-output validation badges. Calls /carerx/ai/assist.
 *
 * `phiValues` are the known patient identifiers (name, MRN, DOB, phone…) to redact before
 * anything leaves the browser.
 */
export default function AiAssistPanel({ context, phiValues = [] }: { context?: string; phiValues?: string[] }) {
  const [open, setOpen] = useState(false)
  const [consented, setConsented] = useState(() => sessionStorage.getItem(CONSENT_KEY) === '1')
  const [input, setInput] = useState('')
  const [log, setLog] = useState<{ role: 'user' | 'ai' | 'sys'; text: string; badge?: string; warnings?: string[] }[]>([])
  const [busy, setBusy] = useState(false)
  const [models, setModels] = useState<api.CareRxAiModel[]>([])
  const [model, setModel] = useState<string>('')

  useEffect(() => {
    if (!open || !consented || models.length) return
    api.getCareRxAiModels().then(r => { setModels(r.models); setModel(m => m || r.default_model) }).catch(() => {})
  }, [open, consented, models.length])

  const giveConsent = () => {
    sessionStorage.setItem(CONSENT_KEY, '1'); setConsented(true)
    api.acceptCareRxAiConsent().catch(() => {/* best-effort audit log */})
  }

  const ask = async (prompt: string, type: 'icd10' | 'ddi' | 'dose' | 'field_assist' | 'chat' = 'chat') => {
    if (!prompt.trim() || busy) return
    setLog(l => [...l, { role: 'user', text: prompt }])
    setInput(''); setBusy(true)
    try {
      // FIRST layer: tokenize known PHI values in the browser before sending.
      const full = context ? `${context}\n\n${prompt}` : prompt
      const { text: safe, map } = tokenizePHI(full, phiValues)
      const r: any = await api.aiAssist([{ role: 'user', content: safe }], undefined, model || undefined)
      const restored = detokenize(r.text || '(no response)', map as TokenMap)
      const v = validateAIOutput(restored, type)
      setLog(l => [...l, { role: 'ai', text: restored, badge: v.badge, warnings: v.warnings }])
    } catch (e: any) {
      const p = e?.body
      if (p?.error === 'ai_not_configured') {
        setLog(l => [...l, { role: 'sys', text: 'AI is not configured yet — a Claude API key + BAA is needed. Your message was PHI-tokenized; nothing was sent externally.' }])
      } else if (p?.error === 'phi_not_allowed') {
        const types = Array.isArray(p?.detected_types) && p.detected_types.length ? ` (${p.detected_types.join(', ')})` : ''
        setLog(l => [...l, { role: 'sys', text: `⚠️ PHI detected${types} — entering protected health information into the AI is not allowed, so nothing was sent. Please remove it and try again. This was recorded in the audit log.` }])
      } else {
        setLog(l => [...l, { role: 'sys', text: 'AI request failed.' }])
      }
    } finally { setBusy(false) }
  }

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} style={fab} title="AI clinical assist">
        <Sparkles size={16} /> AI
      </button>
    )
  }

  return (
    <div style={panel}>
      <div style={head}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 700, fontSize: 13 }}><Sparkles size={15} /> AI clinical assist</span>
        <button style={iconBtn} onClick={() => setOpen(false)}><X size={15} /></button>
      </div>

      {!consented ? (
        <div style={{ padding: '16px' }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
            <ShieldCheck size={18} style={{ color: 'var(--color-primary)', flexShrink: 0 }} />
            <div style={{ fontSize: 12.5, color: 'var(--color-text-soft)', lineHeight: 1.5 }}>
              The AI assistant helps with drug info, ICD-10 lookup, interaction screening, and clinical
              language. Patient identifiers are removed before any request, and AI output must be
              verified by you before use. Each request is logged for your pharmacy's HIPAA audit: who asked,
              when, and the first 500 characters of the de-identified question and answer. Do you consent to
              AI-assisted documentation?
            </div>
          </div>
          <button style={sendBtn2} onClick={giveConsent}>I consent — enable AI assist</button>
        </div>
      ) : (
        <>
          {models.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderBottom: '1px solid var(--color-border)' }}>
              <span style={{ fontSize: 11, color: 'var(--color-text-mute)' }}>Model</span>
              <select value={model} onChange={e => setModel(e.target.value)} disabled={busy}
                style={{ flex: 1, fontSize: 12, padding: '4px 7px', borderRadius: 7, border: '1px solid var(--color-border)', background: 'var(--color-surface,#fff)' }}>
                {models.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
              </select>
            </div>
          )}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '10px 12px', borderBottom: '1px solid var(--color-border)' }}>
            {QUICK.map(q => (
              <button key={q.label} style={quick} disabled={busy} onClick={() => ask(q.prompt, q.type)}>{q.label}</button>
            ))}
          </div>
          <div style={body}>
            {log.length === 0 && <div style={{ color: 'var(--color-text-mute)', fontSize: 12 }}>Ask a clinical question, or use a quick action. Patient identifiers are stripped before anything is sent; AI output is advisory — verify before use.</div>}
            {log.map((m, i) => (
              <div key={i} style={{ marginBottom: 10, fontSize: 12.5 }}>
                <div style={{ fontWeight: 600, color: m.role === 'user' ? 'var(--color-text)' : m.role === 'ai' ? 'var(--color-primary)' : '#d97706' }}>
                  {m.role === 'user' ? 'You' : m.role === 'ai' ? 'AI' : 'Note'}
                </div>
                <div style={{ whiteSpace: 'pre-wrap', color: 'var(--color-text-soft)' }}>{m.text}</div>
                {m.badge && <div style={badgeStyle}>⚠ {m.badge}</div>}
                {m.warnings?.map((w, j) => <div key={j} style={{ fontSize: 11, color: '#dc2626', marginTop: 2 }}>{w}</div>)}
              </div>
            ))}
            {busy && <div style={{ display: 'flex', gap: 6, alignItems: 'center', color: 'var(--color-text-mute)', fontSize: 12 }}><Loader2 className="spin" size={14} /> thinking…</div>}
          </div>
          <div style={{ display: 'flex', gap: 6, padding: '10px 12px', borderTop: '1px solid var(--color-border)' }}>
            <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') ask(input) }}
              placeholder="Ask a clinical question…" style={{ flex: 1, padding: '7px 9px', border: '1px solid var(--color-border)', borderRadius: 6, fontSize: 13, background: 'var(--color-surface)', color: 'var(--color-text)' }} />
            <button style={sendBtn} disabled={busy} onClick={() => ask(input)}><Send size={14} /></button>
          </div>
        </>
      )}
    </div>
  )
}

const fab: React.CSSProperties = { position: 'fixed', right: 24, bottom: 24, zIndex: 50, display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 16px', borderRadius: 999, border: 'none', background: 'var(--color-primary)', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer', boxShadow: '0 4px 14px rgba(0,0,0,0.2)' }
const panel: React.CSSProperties = { position: 'fixed', right: 24, bottom: 24, zIndex: 50, width: 340, maxHeight: '70vh', display: 'flex', flexDirection: 'column', background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 12, boxShadow: '0 8px 28px rgba(0,0,0,0.22)' }
const head: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', borderBottom: '1px solid var(--color-border)' }
const body: React.CSSProperties = { flex: 1, overflowY: 'auto', padding: '12px', minHeight: 80 }
const quick: React.CSSProperties = { padding: '5px 10px', borderRadius: 999, border: '1px solid var(--color-primary)', background: 'var(--color-surface)', color: 'var(--color-primary)', fontSize: 11.5, fontWeight: 600, cursor: 'pointer' }
const sendBtn: React.CSSProperties = { padding: '7px 11px', borderRadius: 6, border: 'none', background: 'var(--color-primary)', color: '#fff', cursor: 'pointer' }
const iconBtn: React.CSSProperties = { background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-mute)' }
const badgeStyle: React.CSSProperties = { display: 'inline-block', marginTop: 4, fontSize: 10.5, fontWeight: 600, color: '#d97706', border: '1px solid #d97706', borderRadius: 999, padding: '1px 8px' }
const sendBtn2: React.CSSProperties = { width: '100%', padding: '9px 14px', borderRadius: 8, border: 'none', background: 'var(--color-primary)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }
